// src/lib/leadDedupe.js
// Failsafe: prevent the same property from being entered twice into the
// HomeIQa pipeline. We check by normalized street address (line1+city+state)
// AND by normalized listing URL. If either matches an existing lead, we
// surface a custom modal instead of letting the dupe through.

import { collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Strip everything that's just noise so two visually-identical addresses
// match even if one has a trailing comma or different casing.
export function normalizeAddress(line1, city, state) {
  const norm = (s) => String(s || '')
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\bstreet\b/g, 'st')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/\broad\b/g, 'rd')
    .replace(/\bdrive\b/g, 'dr')
    .replace(/\blane\b/g, 'ln')
    .replace(/\bboulevard\b/g, 'blvd')
    .replace(/\bcourt\b/g, 'ct')
    .replace(/\bcircle\b/g, 'cir')
    .replace(/\bplace\b/g, 'pl')
    .replace(/\bterrace\b/g, 'ter')
    .replace(/\bparkway\b/g, 'pkwy')
    .replace(/\bhighway\b/g, 'hwy')
    .replace(/\bnorth\b/g, 'n')
    .replace(/\bsouth\b/g, 's')
    .replace(/\beast\b/g, 'e')
    .replace(/\bwest\b/g, 'w')
    .replace(/\s+/g, ' ')
    .trim();
  return [norm(line1), norm(city), norm(state)].filter(Boolean).join('|');
}

// Strip query params, fragments, "www.", trailing slashes — match URL
// equality across copy/paste variations.
export function normalizeUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(String(url).trim());
    let host = u.hostname.toLowerCase().replace(/^www\./, '');
    let path = u.pathname.replace(/\/+$/, '').toLowerCase();
    return `${host}${path}`;
  } catch {
    return String(url).trim().toLowerCase().replace(/[?#].*$/, '').replace(/\/+$/, '');
  }
}

// Search across every agent's pipeline (collectionGroup) for any lead
// whose normalized address or URL matches. Returns the first match or
// null. Excludes the lead with id == excludeLeadId (used when editing
// an existing lead, so a "no change" save doesn't trip the dedupe).
//
// Returns: { id, leadId, ownerUid, address, listingUrl, status, submittedByName, matchedOn }
export async function findDuplicateLead({ address, listingUrl, excludeLeadId = null }) {
  const targetAddrKey = address ? normalizeAddress(address.line1, address.city, address.state) : '';
  const targetUrlKey  = normalizeUrl(listingUrl);
  if (!targetAddrKey && !targetUrlKey) return null;

  let snap;
  try {
    snap = await getDocs(collectionGroup(db, 'leads'));
  } catch (e) {
    // If the user doesn't have permission for collectionGroup, fall back to null.
    // Server-side dedupe via Cloud Function would be the next step here, but for
    // now this is a UX nicety, not a security guarantee.
    console.warn('Dedupe lookup failed:', e.message);
    return null;
  }

  for (const docSnap of snap.docs) {
    if (excludeLeadId && docSnap.id === excludeLeadId) continue;
    const d = docSnap.data();
    const addrKey = normalizeAddress(d.address?.line1, d.address?.city, d.address?.state);
    const urlKey  = normalizeUrl(d.listingUrl);
    const addrMatch = !!targetAddrKey && targetAddrKey === addrKey;
    const urlMatch  = !!targetUrlKey  && targetUrlKey  === urlKey;
    if (addrMatch || urlMatch) {
      return {
        id: docSnap.id,
        leadId: docSnap.id,
        ownerUid: docSnap.ref.parent.parent.id,
        address: d.address || {},
        listingUrl: d.listingUrl || '',
        status: d.status || 'New',
        submittedByName: d.submittedBy?.name || null,
        ownerNames: (d.owners || []).map(o => o.name).filter(Boolean),
        createdAt: d.createdAt || null,
        matchedOn: addrMatch && urlMatch ? 'address+url' : addrMatch ? 'address' : 'url'
      };
    }
  }
  return null;
}

// Lightweight reusable modal (custom, no browser alerts) shown when a
// duplicate is found. Lives next to the helper so callers import one
// file. Uses inline styles to stay consistent with the rest of the
// agent UI.
//
// Props: { duplicate, onClose, onView, onSaveAnyway } — onSaveAnyway
// is optional; if omitted the user can only Close or View existing.
import React from 'react';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const CREAM = '#FAF7F2';
const BORDER = 'rgba(107,30,46,0.1)';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';

export function DuplicateLeadModal({ duplicate, onClose, onView, onSaveAnyway }) {
  if (!duplicate) return null;
  const matchLabel =
    duplicate.matchedOn === 'address+url' ? 'address AND listing URL'
    : duplicate.matchedOn === 'address' ? 'address'
    : 'listing URL';

  const fullAddr = [duplicate.address?.line1, duplicate.address?.city, duplicate.address?.state, duplicate.address?.zip]
    .filter(Boolean).join(', ');

  return (
    <div style={d.backdrop} onClick={onClose}>
      <div style={d.panel} onClick={e => e.stopPropagation()}>
        <div style={d.head}>
          <div style={d.eyebrow}>⚠ Duplicate detected</div>
          <button onClick={onClose} style={d.x} aria-label="Close">×</button>
        </div>
        <div style={d.body}>
          <p style={d.lead}>
            This lead already exists in the HomeIQa pipeline — we matched on the <strong>{matchLabel}</strong>.
          </p>
          <div style={d.meta}>
            <div style={d.metaRow}><span style={d.metaLabel}>Address</span><span style={d.metaValue}>{fullAddr || '—'}</span></div>
            {duplicate.listingUrl && (
              <div style={d.metaRow}><span style={d.metaLabel}>Listing</span><a href={duplicate.listingUrl} target="_blank" rel="noreferrer" style={{ ...d.metaValue, color: BURG, textDecoration: 'underline' }}>open ↗</a></div>
            )}
            {duplicate.ownerNames?.length > 0 && (
              <div style={d.metaRow}><span style={d.metaLabel}>Owner(s)</span><span style={d.metaValue}>{duplicate.ownerNames.join(', ')}</span></div>
            )}
            <div style={d.metaRow}><span style={d.metaLabel}>Status</span><span style={d.metaValue}>{duplicate.status}</span></div>
            {duplicate.submittedByName && (
              <div style={d.metaRow}><span style={d.metaLabel}>Submitted by</span><span style={d.metaValue}>{duplicate.submittedByName}</span></div>
            )}
            {duplicate.createdAt?.toDate && (
              <div style={d.metaRow}><span style={d.metaLabel}>Added</span><span style={d.metaValue}>{duplicate.createdAt.toDate().toLocaleDateString()}</span></div>
            )}
          </div>
          <p style={d.tip}>
            Don't enter the same property twice — work the existing lead instead. If this is genuinely a different property at the same address (rare), you can override below.
          </p>
        </div>
        <div style={d.footer}>
          <button onClick={onClose} style={d.btnGhost}>Cancel</button>
          {onView && <button onClick={() => onView(duplicate)} style={d.btnGold}>Open existing lead →</button>}
          {onSaveAnyway && <button onClick={onSaveAnyway} style={d.btnDanger}>Save anyway</button>}
        </div>
      </div>
    </div>
  );
}

const d = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', backdropFilter: 'blur(2px)', zIndex: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" },
  head: { padding: '14px 22px', background: '#FEF3C7', borderBottom: '1px solid #FCD34D', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  eyebrow: { fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#92400E' },
  x: { background: 'transparent', border: 'none', fontSize: 26, lineHeight: 1, color: '#92400E', cursor: 'pointer', padding: 0, width: 28 },
  body: { padding: '20px 22px' },
  lead: { fontSize: 14, color: BURG_DK, lineHeight: 1.6, margin: '0 0 16px' },
  meta: { background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 },
  metaRow: { display: 'flex', alignItems: 'baseline', gap: 12, padding: '4px 0', fontSize: 12 },
  metaLabel: { color: LIGHT, textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.14em', minWidth: 86 },
  metaValue: { color: BURG_DK, flex: 1, fontWeight: 500 },
  tip: { fontSize: 12, color: MID, lineHeight: 1.6, margin: 0 },
  footer: { padding: '14px 22px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' },
  btnGhost: { background: 'transparent', color: BURG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  btnGold: { background: GOLD, color: BURG_DK, border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  btnDanger: { background: '#fff', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};
